/* eslint-disable max-statements */
/* eslint-disable react/jsx-props-no-spreading */
import { Alert, Button, FloatingLabel, Form, Spinner } from "react-bootstrap";
import { useCallback, useMemo } from "react";
import { useMutation, useQueryClient } from "react-query";
import { Formik } from "formik";
import PropTypes from "prop-types";
import { camelCase } from "lodash";
import classNames from "classnames";

import { assocFields, labelText, shouldHide } from "../../../components/ACF/acfHelpers";
import ACFCheckboxes from "../../../components/ACF/ACFCheckboxes";
import ACFInput from "../../../components/ACF/ACFInput";
import acfGroup from "../../../acf-json/group_631a634d16847.json";
import { shouldAttendeeCompleteDemo } from "../../../helpers/attendeeHelpers";
import { updateUserMutation } from "../../../services/wp.user.service";
import { useAuth } from "../../../services/wp.auth.service";
import { useNotifications } from "../../../components/NotificationsContext";

const formFields = acfGroup.fields.filter(({ type }) => type !== "true_false");
const aFields = assocFields(formFields);

const DemographicsForm = ({
	onSuccess,
	refetchUser
}) => {
	const { user } = useAuth();
	const { addNotification } = useNotifications();
	const queryClient = useQueryClient();
	const incompleteDemographics = useMemo(() => shouldAttendeeCompleteDemo(user), [user]);

	const {
		isLoading,
		mutateAsync: saveDemographics
	} = updateUserMutation(useMutation);

	const initialValues = useMemo(() => user.attendeeDemographics, [user]);
	const submitForm = useCallback(async (attendeeDemographics) => {
		attendeeDemographics.demographicsCompleted = true;

		try {
			await saveDemographics({
				id: user.databaseId,
				attendeeDemographics
			});
			if (refetchUser) {
				await queryClient.refetchQueries("auth-user");
			}
			addNotification({
				message: "Successfully saved demographics.",
				type: "success"
			});
			if (onSuccess) {
				onSuccess();
			}
		} catch (error) {
			addNotification({
				message: error.message || "Errors saving demographics information.",
				type: "danger"
			});
		}
	}, []);

	return (
		<>
			{user?.attendeeDemographics?.demographicsCompleted && incompleteDemographics
				? (
					<Alert variant="warning">
						Please confirm your demographics information as some of the options may have changed.
					</Alert>
				) : null}
			<Formik
				initialValues={initialValues}
				onSubmit={submitForm}
			>
				{({
					handleBlur,
					handleChange,
					handleSubmit,
					values
				}) => (
					<Form onSubmit={handleSubmit}>
						{formFields.map((field) => {
							const label = useMemo(() => labelText(field.label, field.required), [field.label]);
							return field.type === "checkbox"
								? (
									<ACFCheckboxes
										onChange={handleChange}
										{
											...{
												className: "mb-4",
												...field,
												required: !!field.required,
												value: values[camelCase(field.name)]
											}
										}
									/>
								)
								: (
									<FloatingLabel
										className={classNames("mb-4")}
										// eslint-disable-next-line react/jsx-no-leaked-render
										hidden={field.conditional_logic && shouldHide(field.conditional_logic, values, aFields)}
										id={camelCase(field.name)}
										key={field.name}
										label={label}
										placeholder={label}
									>
										<ACFInput
											{...field}
											name={camelCase(field.name)}
											onBlur={handleBlur}
											onChange={handleChange}
											placeholder={label}
											required={!!field.required}
											value={values[camelCase(field.name)] || ""}
										/>
									</FloatingLabel>
								);
						})}
						<div className="text-end">
							<Button disabled={isLoading} type="submit">
								{isLoading ? <Spinner animation="border" size="sm" /> : "Save Demographics Information"}
							</Button>
						</div>
					</Form>
				)}
			</Formik>
		</>
	);
};

DemographicsForm.propTypes = {
	onSuccess: PropTypes.func,
	refetchUser: PropTypes.bool
};

DemographicsForm.defaultProps = {
	onSuccess: null,
	refetchUser: true
};

export default DemographicsForm;
